import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Calendar, Clock } from "lucide-react";
import { getPostBySlug, posts } from "@/data/posts";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <section className="pt-40 pb-24 bg-bone-100 min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-display text-5xl text-ink-900">Beitrag nicht gefunden</h1>
          <p className="mt-5 text-ink-700">Dieser Beitrag existiert nicht oder wurde verschoben.</p>
          <Link to="/beitraege" className="mt-8 inline-flex items-center gap-2 text-sage-600 link-underline">
            <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Zurück zu allen Beiträgen
          </Link>
        </div>
      </section>
    );
  }

  const related = posts.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 2);

  return (
    <>
      <section className="relative pt-32 pb-8 md:pt-40 overflow-hidden bg-bone-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <nav className="flex items-center gap-2 font-mono-fine text-[11px] tracking-[0.2em] uppercase text-ink-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-ink-900 transition-colors">Start</Link>
            <span>›</span>
            <Link to="/beitraege" className="hover:text-ink-900 transition-colors">Beiträge</Link>
            <span>›</span>
            <span className="text-ink-900 truncate max-w-[180px]">{post.category}</span>
          </nav>

          <span className={`inline-flex items-center gap-2 px-3 h-7 rounded-full font-mono-fine text-[10px] tracking-[0.2em] uppercase ${post.chip}`}>
            {post.category}
          </span>

          <h1 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[4rem] leading-[1] tracking-[-0.035em] text-ink-900">
            {post.title}
          </h1>

          <div className="mt-8 flex items-center gap-6 font-mono-fine text-[11px] tracking-[0.18em] uppercase text-ink-500">
            <span className="flex items-center gap-2">
              <Calendar strokeWidth={1.75} className="w-3.5 h-3.5" /> {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock strokeWidth={1.75} className="w-3.5 h-3.5" /> {post.read}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-bone-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-[2rem] overflow-hidden shadow-lift">
            <img src={post.img} alt={post.title} className="w-full h-[340px] md:h-[460px] object-cover" />
          </div>
        </div>
      </section>

      <article className="relative py-20 md:py-24 bg-bone-100">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 space-y-6 text-ink-700 text-[18px] leading-[1.85]">
          {post.content.map((b, i) => {
            if (b.type === "h") {
              return (
                <h2 key={i} className="font-display font-light text-3xl md:text-4xl text-ink-900 leading-tight mt-10 tracking-[-0.02em]">
                  {b.text}
                </h2>
              );
            }
            if (b.type === "list") {
              return (
                <ul key={i} className="space-y-3 pl-0">
                  {b.items.map((it) => (
                    <li key={it} className="flex gap-3">
                      <span className="mt-[11px] w-1.5 h-1.5 rounded-full bg-sage-400 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return <p key={i}>{b.text}</p>;
          })}
        </div>
      </article>

      {related.length > 0 && (
        <section className="relative py-20 md:py-24 bg-sage-50 border-t border-bone-300">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-4 mb-10">
              <span className="h-[1px] w-10 bg-sage-400" />
              <span className="font-mono-fine text-[11px] tracking-[0.28em] uppercase text-sage-600">Weiterlesen</span>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/beitraege/${r.slug}`}
                  className="group bg-bone-50 rounded-[1.5rem] overflow-hidden border border-bone-300 hover:shadow-lift transition-all duration-500 grid sm:grid-cols-2"
                >
                  <img src={r.img} alt={r.title} className="w-full h-48 sm:h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  <div className="p-6">
                    <span className={`inline-flex items-center px-2.5 h-6 rounded-full font-mono-fine text-[10px] tracking-[0.2em] uppercase ${r.chip}`}>
                      {r.category}
                    </span>
                    <h3 className="mt-3 font-display text-[1.4rem] leading-tight text-ink-900 group-hover:text-sage-700 transition-colors">{r.title}</h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] tracking-[0.12em] uppercase font-mono-fine text-ink-900">
                      Weiterlesen <ArrowUpRight strokeWidth={1.5} className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-bone-100 border-t border-bone-300">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Link to="/beitraege" className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase font-mono-fine text-ink-900 link-underline">
            <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Alle Beiträge
          </Link>
        </div>
      </section>
    </>
  );
}
